import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { activitiesAPI, hotelsAPI, packagesAPI } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import PackageCard, { formatMoney, parseMoney } from './PackageCard';

const WHATSAPP = '919459457733';
const PHONE_DISPLAY = '+91 945 945 7733';

const SERVICE_CARDS = [
  {
    title: 'Customised Goa Packages',
    text: 'Flexible packages designed around your dates, group size, budget, meals, transfers, tours, and activities.',
    icon: 'fa-suitcase-rolling',
    to: '/packages',
  },
  {
    title: 'Hotel Booking',
    text: 'Comfortable stays in and around Calangute with room categories for couples, families, and groups.',
    icon: 'fa-hotel',
    to: '/hotels-list',
  },
  {
    title: 'Cruises & Activities',
    text: 'Dinner cruises, adventure boats, waterfall trips, seasonal water activities, and more.',
    icon: 'fa-ship',
    to: '/activities',
  },
  {
    title: 'Sightseeing Tours',
    text: 'Private and sharing North Goa and South Goa sightseeing options with local coordination.',
    icon: 'fa-map-marked-alt',
    to: '/tours',
  },
  {
    title: 'Private Transfers',
    text: 'Airport, railway, bus stand, and local transfers with vehicles suited to your group size.',
    icon: 'fa-car-side',
    to: '/taxi',
  },
  {
    title: 'Group Travel',
    text: 'Rooms, meals, buses, tours, and full trip coordination for families, corporates, and agents.',
    icon: 'fa-users',
    to: '/packages',
  },
];

const SIGHTSEEING_TOURS = [
  {
    title: 'North Goa Sightseeing',
    text: 'Forts, churches, beaches, and markets across North Goa on a private or sharing basis.',
  },
  {
    title: 'South Goa Sightseeing',
    text: 'Old Goa churches, temples, and the quieter South Goa beaches with a full-day itinerary.',
  },
  {
    title: 'Dudhsagar Waterfall Trip',
    text: 'Jeep safari and waterfall visit with pick-up from your hotel, subject to season and permits.',
  },
  {
    title: 'Private or Sharing Transport',
    text: 'Choose a private vehicle for your group or a sharing tour to keep costs lower.',
  },
];

const WHY_POINTS = [
  {
    title: 'Local Goa Support',
    text: 'Our team is based in Goa and provides practical local assistance during your stay.',
  },
  {
    title: 'Customised Packages',
    text: 'Built around your dates, guests, rooms, meals, transport, sightseeing, and activity preferences.',
  },
  {
    title: 'Complete Travel Services',
    text: 'Accommodation, transfers, sightseeing, cruises, activities, rentals, and group transportation.',
  },
  {
    title: 'Clear Package Details',
    text: 'Proposals mention room type, meal plan, transport, tour duration, inclusions, and price.',
  },
  {
    title: 'Options for Every Traveller',
    text: 'Couples, families, friends, corporate teams, student groups, and travel agents.',
  },
  {
    title: 'Convenient Communication',
    text: 'Reach us directly by phone, WhatsApp, Instagram, or the website enquiry form.',
  },
];

const HOW_STEPS = [
  {
    n: '01',
    title: 'Share Your Trip Details',
    text: 'Send check-in/out dates, guests, rooms, arrival point, meals, tours, and budget.',
  },
  {
    n: '02',
    title: 'Receive a Customised Package',
    text: 'Our team prepares a proposal based on your requirements and available services.',
  },
  {
    n: '03',
    title: 'Review the Inclusions',
    text: 'Check room category, meal plan, transport, timings, activities, price, and payment terms.',
  },
  {
    n: '04',
    title: 'Confirm & Enjoy Goa',
    text: 'Approve the package, pay the advance, and enjoy coordinated local support on your trip.',
  },
];

const FAQS = [
  {
    q: 'Do you provide customised Goa packages?',
    a: 'Yes. Packages are prepared according to travel dates, number of travellers, rooms, meals, transportation, sightseeing, activities, and budget.',
  },
  {
    q: 'Do you arrange airport and railway station transfers?',
    a: 'Yes. Private pick-up and drop can be arranged from Goa airports, railway stations, bus stands, hotels, and other agreed locations.',
  },
  {
    q: 'Are sightseeing tours private or sharing?',
    a: 'Both options may be available. The selected transport type is clearly mentioned in the package proposal.',
  },
  {
    q: 'Can breakfast and dinner be included?',
    a: 'Yes. Packages may be offered on room-only, breakfast-inclusive, or breakfast-and-dinner plans.',
  },
  {
    q: 'Do you provide packages for families and groups?',
    a: 'Yes. We arrange packages for couples, families, friends, corporate groups, students, and travel agents.',
  },
  {
    q: 'How do I confirm my booking?',
    a: 'Approve the final package and pay the required advance using the authorised payment method shared by GoaDeals. A written confirmation is issued after payment verification.',
  },
];

const PACKAGE_FILTERS = ['All', 'Adventure', 'Family', 'Honeymoon', 'Group'];

const openWhatsApp = (message) => {
  const url = `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(message)}`;
  window.open(url, '_blank', 'noopener,noreferrer');
};

const Home = () => {
  const [loading, setLoading] = useState(true);
  const [hotels, setHotels] = useState([]);
  const [packages, setPackages] = useState([]);
  const [activities, setActivities] = useState([]);
  const [filter, setFilter] = useState('All');
  const [openFaq, setOpenFaq] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    const toList = (payload, keys = []) => {
      if (Array.isArray(payload)) return payload;
      if (!payload || typeof payload !== 'object') return [];
      for (const key of keys) {
        if (Array.isArray(payload[key])) return payload[key];
      }
      if (Array.isArray(payload.data)) return payload.data;
      return [];
    };

    const load = async () => {
      setLoading(true);
      setError('');

      const results = await Promise.allSettled([
        hotelsAPI.getAll(),
        packagesAPI.getAll(),
        activitiesAPI.getAll(),
      ]);

      const [hotelsResult, packagesResult, activitiesResult] = results;

      if (hotelsResult.status === 'fulfilled') {
        setHotels(toList(hotelsResult.value.data, ['hotels']));
      } else {
        setHotels([]);
      }

      if (packagesResult.status === 'fulfilled') {
        setPackages(toList(packagesResult.value.data, ['packages']));
      } else {
        setPackages([]);
      }

      if (activitiesResult.status === 'fulfilled') {
        setActivities(toList(activitiesResult.value.data, ['activities']));
      } else {
        setActivities([]);
      }

      if (results.every((r) => r.status === 'rejected')) {
        setError('Unable to load live listings. You can still explore packages and contact us.');
      }

      setLoading(false);
    };

    load();
  }, []);

  useEffect(() => {
    const hash = window.location.hash?.replace('#', '');
    if (!hash) return;
    const t = setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
    return () => clearTimeout(t);
  }, [loading]);

  const mostSellerHotels = useMemo(() => {
    const list = (hotels || []).filter((x) => x?.mostSeller);
    const base = list.length ? list : hotels || [];
    return base.slice(0, 12);
  }, [hotels]);
  const mostSellerPackages = useMemo(() => {
    const list = (packages || []).filter((x) => x?.mostSeller);
    const base = list.length ? list : packages || [];
    if (filter === 'All') return base.slice(0, 12);
    return base
      .filter((p) => (p.category || '').toLowerCase().includes(filter.toLowerCase()))
      .slice(0, 12);
  }, [packages, filter]);
  const mostSellerActivities = useMemo(() => {
    const list = (activities || []).filter((x) => x?.mostSeller);
    const base = list.length ? list : activities || [];
    return base.slice(0, 12);
  }, [activities]);

  const quoteMessage =
    'Hello GoaDeals, I would like a customised Goa package based on my travel dates, group size, hotel preference, sightseeing, and budget.';

  return (
    <div className="gd-home">
      <Helmet>
        <title>GoaDeals | Goa Tour Packages, Hotels, Sightseeing &amp; Activities</title>
        <meta
          name="description"
          content="Book customised Goa holiday packages, hotels in Calangute, private transfers, North and South Goa tours, dinner cruises, and activities with local Goa support."
        />
      </Helmet>

      {/* Full-bleed video hero — one composition */}
      <section className="gd-hero" aria-label="GoaDeals home hero">
        <video
          className="gd-hero-video"
          autoPlay
          muted
          loop
          playsInline
          poster="/images/banner1.jpg"
        >
          <source
            src="https://videos.pexels.com/video-files/2169880/2169880-uhd_2560_1440_30fps.mp4"
            type="video/mp4"
          />
        </video>
        <div className="gd-hero-shade" />
        <div className="gd-hero-content">
          <img src="/images/logo.png" alt="GoaDeals" className="gd-hero-brand" />
          <h1 className="gd-hero-title">Explore Goa Your Way</h1>
          <p className="gd-hero-lead">
            Affordable hotels, customised holiday packages, private transfers, sightseeing, cruises,
            and activities—through one reliable local travel partner.
          </p>
          <div className="gd-hero-cta">
            <button type="button" className="gd-btn gd-btn-primary" onClick={() => openWhatsApp(quoteMessage)}>
              Get a Free Quote
            </button>
            <Link to="/packages" className="gd-btn gd-btn-ghost">
              Explore Goa Packages
            </Link>
          </div>
        </div>
      </section>

      {/* Packages — CaptureATrip style listing */}
      <section className="gd-section gd-packages" id="packages">
        <div className="container">
          <div className="gd-section-top">
            <div>
              <p className="gd-kicker">Goa Packages</p>
              <h2 className="gd-heading">Explore all Goa tour packages</h2>
            </div>
            <Link to="/packages" className="gd-link-all">
              See all packages
            </Link>
          </div>

          <div className="cat-main">
            <div className="gd-filters cat-pills" role="tablist" aria-label="Package filters">
              {PACKAGE_FILTERS.map((f) => (
                <button
                  key={f}
                  type="button"
                  role="tab"
                  aria-selected={filter === f}
                  className={`gd-filter ${filter === f ? 'is-active' : ''}`}
                  onClick={() => setFilter(f)}
                >
                  {f}
                </button>
              ))}
            </div>

            {loading ? (
              <LoadingSpinner text="Loading packages..." />
            ) : (
              <div className={`cat-grid${mostSellerPackages.length > 3 ? ' is-hscroll' : ''}`}>
                {mostSellerPackages.length === 0 ? (
                  <p className="gd-empty">
                    {error || 'Packages will appear here once available. WhatsApp us for a custom quote.'}
                  </p>
                ) : (
                  mostSellerPackages.map((p) => (
                    <PackageCard key={p._id || p.id} pkg={p} />
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Hotels */}
      <section className="gd-section gd-listings" id="hotels">
        <div className="container">
          <div className="gd-section-top">
            <div>
              <p className="gd-kicker">Most Seller</p>
              <h2 className="gd-heading">Hotels &amp; stays guests love</h2>
            </div>
            <Link to="/hotels-list" className="gd-link-all">
              See all hotels
            </Link>
          </div>

          <div className="gd-listing-block">
            <div className={`cat-grid${mostSellerHotels.length > 3 ? ' is-hscroll' : ''}`}>
              {mostSellerHotels.length === 0 ? (
                <p className="gd-empty">No featured stays yet.</p>
              ) : (
                mostSellerHotels.map((h) => {
                  const current = parseMoney(h.currentPrice);
                  const original = parseMoney(h.originalPrice);
                  const off = original > current ? original - current : 0;
                  return (
                    <Link key={h._id || h.id} to={`/hotel/${h._id || h.id}`} className="cat-card">
                      <div className="cat-card-media">
                        <div className="cat-card-img">
                          <img
                            src={
                              (h.images?.[0] && String(h.images[0]).replace(/ /g, '%20')) ||
                              'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&h=600&fit=crop'
                            }
                            alt={h.name}
                          />
                          {h.mostSeller && <span className="cat-badge">Most Seller</span>}
                        </div>
                        <span className="cat-duration">
                          <span aria-hidden="true">🏨</span> {(h.category || 'Hotel').toString()}
                        </span>
                      </div>
                      <div className="cat-card-info">
                        <h3 className="cat-title">{h.name}</h3>
                        <div className="cat-price-block">
                          <span className="cat-price">{formatMoney(current)}</span>
                          {original > current && (
                            <>
                              <span className="cat-old">{formatMoney(original)}</span>
                              <span className="cat-off">{formatMoney(off)} Off</span>
                            </>
                          )}
                        </div>
                        <p className="cat-dates">
                          <span aria-hidden="true">📍</span> {h.location || 'Goa'}
                        </p>
                      </div>
                    </Link>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Activities */}
      <section className="gd-section gd-listings gd-listings-alt" id="activities">
        <div className="container">
          <div className="gd-section-top">
            <div>
              <p className="gd-kicker">Cruises &amp; Activities</p>
              <h2 className="gd-heading">Things to do in Goa</h2>
            </div>
            <Link to="/activities" className="gd-link-all">
              See all activities
            </Link>
          </div>

          <div className="gd-listing-block">
            <div className={`cat-grid${mostSellerActivities.length > 3 ? ' is-hscroll' : ''}`}>
              {mostSellerActivities.length === 0 ? (
                <p className="gd-empty">No featured activities yet.</p>
              ) : (
                mostSellerActivities.map((a) => (
                  <Link key={a._id || a.id} to="/activities" className="cat-card">
                    <div className="cat-card-media">
                      <div className="cat-card-img">
                        <img
                          src={a.images?.[0] || 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&h=600&fit=crop'}
                          alt={a.activityName}
                        />
                        {a.mostSeller && <span className="cat-badge">Most Seller</span>}
                      </div>
                      <span className="cat-duration">
                        <span aria-hidden="true">🎯</span> Activity
                      </span>
                    </div>
                    <div className="cat-card-info">
                      <h3 className="cat-title">{a.activityName}</h3>
                      <div className="cat-price-block">
                        <span className="cat-price">{formatMoney(a.costing)}</span>
                      </div>
                      <p className="cat-dates">
                        <span aria-hidden="true">📍</span> {a.location || 'Goa'}
                      </p>
                    </div>
                  </Link>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="gd-section gd-intro">
        <div className="container">
          <p className="gd-kicker">GoaDeals Travel Services</p>
          <h2 className="gd-heading">Plan your Goa holiday with a trusted local team</h2>
          <p className="gd-copy">
            We bring accommodation, airport and railway transfers, sightseeing, dinner cruises,
            adventure activities, and group transportation together in one convenient package.
            Whether you are travelling as a couple, family, group of friends, or corporate team,
            we customise your trip according to your needs.
          </p>
        </div>
      </section>

      {/* Popular services */}
      <section className="gd-section gd-services" id="services">
        <div className="container">
          <p className="gd-kicker">Popular Services</p>
          <h2 className="gd-heading">Everything you need for a Goa trip</h2>
          <div className={`gd-service-grid${SERVICE_CARDS.length > 3 ? ' is-hscroll' : ''}`}>
            {SERVICE_CARDS.map((s) => (
              <Link key={s.title} to={s.to} className="gd-service-item">
                <span className="gd-service-icon" aria-hidden="true">
                  <i className={`fas ${s.icon}`} />
                </span>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Sightseeing */}
      <section className="gd-section gd-sight" id="sightseeing">
        <div className="container">
          <div className="gd-section-top">
            <div>
              <p className="gd-kicker">Sightseeing</p>
              <h2 className="gd-heading">North &amp; South Goa tours</h2>
            </div>
            <Link to="/tours" className="gd-link-all">
              See all tours
            </Link>
          </div>
          <div className={`gd-why-grid gd-sight-grid${SIGHTSEEING_TOURS.length > 3 ? ' is-hscroll' : ''}`}>
            {SIGHTSEEING_TOURS.map((t) => (
              <article key={t.title} className="gd-why-item">
                <h3>{t.title}</h3>
                <p>{t.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose */}
      <section className="gd-section gd-why" id="why">
        <div className="container">
          <p className="gd-kicker">Why Choose GoaDeals</p>
          <h2 className="gd-heading">Your local Goa travel partner</h2>
          <p className="gd-copy gd-copy-center">
            Based in Calangute, North Goa, we provide clear package details, reliable service,
            comfortable stays, and proper coordination throughout your trip.
          </p>
          <div className={`gd-why-grid${WHY_POINTS.length > 3 ? ' is-hscroll' : ''}`}>
            {WHY_POINTS.map((w) => (
              <article key={w.title} className="gd-why-item">
                <h3>{w.title}</h3>
                <p>{w.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="gd-section gd-how" id="how-it-works">
        <div className="container">
          <p className="gd-kicker">How It Works</p>
          <h2 className="gd-heading">From enquiry to your Goa holiday</h2>
          <ol className="gd-steps">
            {HOW_STEPS.map((s) => (
              <li key={s.n} className="gd-step">
                <span className="gd-step-n">{s.n}</span>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="gd-section gd-faq" id="faqs">
        <div className="container gd-faq-wrap">
          <p className="gd-kicker">FAQs</p>
          <h2 className="gd-heading">Frequently asked questions</h2>
          <div className="gd-faq-list">
            {FAQS.map((f, i) => (
              <div key={f.q} className={`gd-faq-item ${openFaq === i ? 'is-open' : ''}`}>
                <button
                  type="button"
                  className="gd-faq-q"
                  aria-expanded={openFaq === i}
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                >
                  <span>{f.q}</span>
                  <i className={`fas fa-chevron-${openFaq === i ? 'up' : 'down'}`} aria-hidden="true" />
                </button>
                {openFaq === i && <div className="gd-faq-a">{f.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="gd-cta" id="contact">
        <div className="container gd-cta-inner">
          <h2>Get your customised Goa package</h2>
          <p>
            Share your travel details and receive a package based on your dates, group size,
            hotel preference, sightseeing, and budget.
          </p>
          <div className="gd-cta-actions">
            <button type="button" className="gd-btn gd-btn-primary" onClick={() => openWhatsApp(quoteMessage)}>
              Get My Goa Package
            </button>
            <a className="gd-btn gd-btn-ghost" href={`tel:${PHONE_DISPLAY.replace(/\s/g, '')}`}>
              Call {PHONE_DISPLAY}
            </a>
          </div>
          <p className="gd-cta-meta">
            Calangute, North Goa · WhatsApp {PHONE_DISPLAY} · Instagram @goadeals27
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;
